import { useSessionStorage } from 'usehooks-ts';

import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';
import useUser from 'Utilities/hooks/useUser';
import type { KycStatus } from 'Utilities/types/kyc.types';

import { ALLOWED_KYC_STATUSES_FOR_OTP_MODAL, useHandleKycStatus } from './useHandleKycStatus';

export const QUERYPARAM_ORIGIN_KEY = 'origin';
export const QUERYPARAM_ORIGIN_VALUE = 'emailotpsetup';
export const SESSION_STORAGE_KEY = 'shouldShowOtpSetup';

export enum MODAL_OTP_SETUP_STATUS {
	INCOMPLETE = 'Incomplete',
	SHOWN = 'Shown',
	SUCCESS = 'Success',
}

export const useOtpPhoneSetup = () => {
	const { otpPhone, phoneConfirmedAt } = useUser();
	const hasOtpPhoneAssignedToContact = !!otpPhone;
	const isAssignedOtpPhoneConfirmed = !!phoneConfirmedAt;

	const isPhoneReadyForConfirmation = hasOtpPhoneAssignedToContact && !isAssignedOtpPhoneConfirmed;
	return { isAssignedOtpPhoneConfirmed, isPhoneReadyForConfirmation };
};

export const useIsOtpEnabledUser = () => {
	const isPaymentsOTPEnabled = useFeatureToggle(FEATURES.paymentsOTP);
	const { isLoggedOut } = useUser();
	return isPaymentsOTPEnabled && !isLoggedOut;
};

interface OtpPhoneConfirmationSetup {
	setModalState: (state: MODAL_OTP_SETUP_STATUS) => void;
	shouldShowOtpPhoneConfirmationModal: boolean;
	shouldShowOtpPhoneConfirmationSuccessModal: boolean;
}

export const useOtpPhoneConfirmationSetup = (): OtpPhoneConfirmationSetup => {
	const isOtpEnabledUser = useIsOtpEnabledUser();
	const { kycStatus } = useHandleKycStatus({ source: 'setup' });
	const { isAssignedOtpPhoneConfirmed, isPhoneReadyForConfirmation } = useOtpPhoneSetup();
	const isAllowedKycStatusForOtpModal = ALLOWED_KYC_STATUSES_FOR_OTP_MODAL.has(kycStatus as KycStatus);
	const [modalState, setModalState] = useSessionStorage(SESSION_STORAGE_KEY, MODAL_OTP_SETUP_STATUS.INCOMPLETE);
	const isModalDisplayState =
		modalState === MODAL_OTP_SETUP_STATUS.INCOMPLETE || modalState === MODAL_OTP_SETUP_STATUS.SUCCESS;

	const shouldShowOtpPhoneConfirmationModal =
		isPhoneReadyForConfirmation && isOtpEnabledUser && isAllowedKycStatusForOtpModal && isModalDisplayState;

	const shouldShowOtpPhoneConfirmationSuccessModal =
		(shouldShowOtpPhoneConfirmationModal && isAssignedOtpPhoneConfirmed) ||
		modalState === MODAL_OTP_SETUP_STATUS.SUCCESS;

	return {
		setModalState,
		shouldShowOtpPhoneConfirmationModal,
		shouldShowOtpPhoneConfirmationSuccessModal,
	};
};
